<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="true"
    width="800px"
    class="memory-curve-dialog"
    center
    @close="handleClose"
  >
    <!-- 开始页面 -->
    <div v-if="currentPhase === 'start'" class="start-phase">
      <div class="phase-header">
        <h2>记忆曲线复习</h2>
        <p>基于艾宾浩斯遗忘曲线的智能复习</p>
      </div>

      <div class="start-content">
        <div class="curve-intro">
          <div class="intro-text">
            <h3>记忆曲线原理</h3>
            <p>德国心理学家赫尔曼·艾宾浩斯研究发现，遗忘在学习之后立即开始，而且遗忘的进程并不是均匀的。最初遗忘速度很快，以后逐渐缓慢。</p>
            <p>通过科学的复习间隔，可以有效提高记忆保持率，让学习更加高效。</p>
          </div>
          
          <div class="stats-info">
            <div class="stat-item">
              <span class="stat-label">需要复习的单词:</span>
              <span class="stat-value">{{ reviewWords.length }} 个</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">预计复习时间:</span>
              <span class="stat-value">{{ estimatedTime }} 分钟</span>
            </div>
          </div>
        </div>

        <div class="action-buttons">
          <el-button type="success" size="large" @click="startReview" :disabled="reviewWords.length === 0">
            <i class="el-icon-play"></i>
            开始复习
          </el-button>
          <el-button type="info" size="large" @click="handleClose">
            <i class="el-icon-close"></i>
            稍后再说
          </el-button>
        </div>
      </div>
    </div>

    <!-- 第一部分：看词想义 -->
    <div v-else-if="currentPhase === 'review'" class="review-phase">
      <div class="phase-header">
        <h2>看词想义</h2>
        <div class="progress-info">
          <span>进度: {{ currentWordIndex + 1 }}/{{ localReviewWords.length }}</span>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: reviewProgress + '%' }"></div>
          </div>
        </div>
      </div>

      <div class="word-display">
        <div class="word-card">
          <h1 class="word-text">{{ currentWord.english_word }}</h1>
          <div class="word-info">
            <div class="phonetic-info">
              <span class="phonetic" v-if="currentWord.phonetic_en">英{{ currentWord.phonetic_en }}</span>
              <span class="phonetic" v-if="currentWord.phonetic_us">美{{ currentWord.phonetic_us }}</span>
            </div>
            <el-button type="primary" size="small" @click="playPronunciation">
              <i class="el-icon-microphone"></i>
              播放发音
            </el-button>
          </div>
        </div>

        <div class="meaning-display" v-if="showMeaning">
          <div class="meaning-card">
            <h3>{{ currentWord.chinese_meaning }}</h3>
            <p v-if="currentWord.example_sentence" class="example-sentence">
              {{ currentWord.example_sentence }}
            </p>
            <p v-if="currentWord.chinese_sentence" class="chinese-sentence">
              {{ currentWord.chinese_sentence }}
            </p>
          </div>
        </div>

        <div class="question-area" v-else>
          <div class="question-mark">?</div>
          <p>请回忆这个单词的中文含义</p>
        </div>
      </div>

      <div class="action-buttons">
        <el-button type="success" @click="handleRemember" :disabled="showMeaning || isShowingMeaningDelay">
          <i class="el-icon-check"></i>
          我记得
        </el-button>
        <el-button type="warning" @click="handleForget" :disabled="showMeaning || isShowingMeaningDelay">
          <i class="el-icon-close"></i>
          不记得
        </el-button>
        <el-button type="info" @click="handleSkip" :disabled="showMeaning || isShowingMeaningDelay">
          <i class="el-icon-arrow-right"></i>
          跳过
        </el-button>
        <el-button type="primary" @click="showMeaning = true" v-if="!showMeaning && !isShowingMeaningDelay">
          <i class="el-icon-view"></i>
          显示词义
        </el-button>
        <el-button type="primary" @click="nextWord" v-if="showMeaning && !isShowingMeaningDelay">
          <i class="el-icon-arrow-right"></i>
          下一个
        </el-button>
        <div v-if="isShowingMeaningDelay" class="delay-indicator">
          <i class="el-icon-loading"></i>
          正在显示词义...
        </div>
      </div>
    </div>

    <!-- 第二部分：拼写测试选择 -->
    <div v-else-if="currentPhase === 'test-mode'" class="test-mode-selection">
      <div class="phase-header">
        <h2>拼写测试</h2>
        <p>选择测试模式</p>
      </div>

      <div class="test-options">
        <div class="option-card" @click="startSpellingTest('ordered')">
          <div class="option-icon">
            <i class="el-icon-sort"></i>
          </div>
          <h3>按顺序测试</h3>
          <p>按照当前顺序进行拼写测试</p>
        </div>

        <div class="option-card" @click="startSpellingTest('random')">
          <div class="option-icon">
            <i class="el-icon-refresh"></i>
          </div>
          <h3>随机测试</h3>
          <p>随机打乱顺序进行拼写测试</p>
        </div>
      </div>

      <div class="action-buttons">
        <el-button type="info" @click="handleClose">
          <i class="el-icon-close"></i>
          结束复习
        </el-button>
      </div>
    </div>

    <!-- 拼写测试 -->
    <div v-else-if="currentPhase === 'spelling'" class="spelling-phase">
      <div class="phase-header">
        <h2>拼写测试</h2>
        <div class="progress-info">
          <span>进度: {{ currentWordIndex + 1 }}/{{ localReviewWords.length }}</span>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: spellingProgress + '%' }"></div>
          </div>
        </div>
      </div>

      <div class="spelling-interface">
        <div class="word-info">
          <div class="word-row">
            <h3>{{ currentWord.chinese_meaning }}</h3>
            <el-button type="primary" size="small" @click="playPronunciation">
              <i class="el-icon-microphone"></i>
              播放发音
            </el-button>
          </div>
        </div>
        <div class="input-area">
          <div class="timer-display" v-if="!showAnswer">
            <span class="timer-text">剩余时间: {{ timeLeft }}秒</span>
            <div class="timer-bar">
              <div class="timer-fill" :style="{ width: (timeLeft / 10) * 100 + '%' }"></div>
            </div>
          </div>
          <div class="input-container">
            <input
              v-model="userInput"
              @keyup.enter="checkSpelling"
              @paste.prevent
              @copy.prevent
              @cut.prevent
              type="text"
              placeholder="请输入英文单词..."
              class="spelling-input"
              :class="{ correct: showAnswer && isCorrect, incorrect: showAnswer && !isCorrect }"
              :disabled="showAnswer"
            />
            <el-button type="primary" @click="checkSpelling" :disabled="!userInput.trim() || showAnswer">
              提交
            </el-button>
          </div>
        </div>
        <div class="answer-display" v-if="showAnswer">
          <div class="answer-result" :class="{ correct: isCorrect, incorrect: !isCorrect }">
            {{ isCorrect ? '正确！' : '错误，正确答案是：' }}
            <span v-if="!isCorrect" class="correct-answer">{{ currentWord.english_word }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 强制拼写 -->
    <div v-else-if="currentPhase === 'forced-spelling'" class="forced-spelling-phase">
      <div class="phase-header">
        <h2>强制拼写</h2>
        <p>请按顺序输入字母，只能输入正确的字母</p>
      </div>

      <div class="forced-spelling-interface">
        <div class="word-info">
          <h3>{{ currentWord.chinese_meaning }}</h3>
          <div class="progress-info">
            进度: {{ forcedSpellingIndex }}/{{ currentWord.english_word.length }}
          </div>
        </div>

        <div class="input-area">
          <input
            v-model="userInput"
            @keydown="handleForcedSpellingInput"
            type="text"
            placeholder="请按顺序输入字母..."
            class="forced-spelling-input"
            :disabled="forcedSpellingIndex >= currentWord.english_word.length"
          />
          <div class="expected-char" v-if="forcedSpellingIndex < currentWord.english_word.length">
            下一个字母: {{ currentWord.english_word[forcedSpellingIndex].toUpperCase() }}
          </div>
        </div>
      </div>
    </div>

    <!-- 学习小结 -->
    <div v-else-if="currentPhase === 'summary'" class="summary-phase">
      <div class="phase-header">
        <h2>学习小结</h2>
      </div>

      <div class="summary-content">
        <div class="stats-grid">
          <div class="stat-item">
            <span class="stat-label">记忆夹生词 (个):</span>
            <span class="stat-value">{{ summaryStats.memorizedWords }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">拼写夹生词 (个):</span>
            <span class="stat-value">{{ summaryStats.spelledWords }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">连续识别错误 (个):</span>
            <span class="stat-value" :class="{ error: summaryStats.consecutiveErrors > 0 }">
              {{ summaryStats.consecutiveErrors }}
            </span>
          </div>
          <div class="stat-item">
            <span class="stat-label">连续拼写错误 (个):</span>
            <span class="stat-value" :class="{ error: summaryStats.consecutiveSpellingErrors > 0 }">
              {{ summaryStats.consecutiveSpellingErrors }}
            </span>
          </div>
          <div class="stat-item">
            <span class="stat-label">总复习时间:</span>
            <span class="stat-value">{{ Math.floor(summaryStats.totalReviewTime / 60) }}分{{ summaryStats.totalReviewTime % 60 }}秒</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">学习准确率:</span>
            <span class="stat-value" :class="{ success: summaryStats.accuracy >= 80 }">
              {{ summaryStats.accuracy }}%
            </span>
          </div>
          <div class="stat-item">
            <span class="stat-label">获得金币:</span>
            <span class="stat-value coins">{{ summaryStats.earnedCoins }}</span>
          </div>
        </div>

        <div class="reward-section">
          <div class="reward-text">
            本次学习/复习将收获 {{ summaryStats.earnedCoins }}
            <i class="el-icon-coin"></i>
          </div>
        </div>

        <div class="action-buttons">
          <el-button type="success" size="large" @click="completeReview">
            <i class="el-icon-check"></i>
            保存退出
          </el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { homeApi } from "@/api/home";

export default {
  name: "MemoryCurveDialog",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    reviewWords: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      dialogVisible: false,
      
      // 阶段控制
      currentPhase: 'start', // start, review, test-mode, spelling, forced-spelling, summary
      
      // 复习相关
      localReviewWords: [],
      currentWordIndex: 0,
      currentWord: {},
      showMeaning: false,
      forgetCount: 0,
      
      // 拼写测试相关
      spellingProgress: 0,
      userInput: '',
      showAnswer: false,
      isCorrect: false,
      spellingTimer: null,
      timeLeft: 10,
      
      // 强制拼写相关
      forcedSpellingIndex: 0,
      
      // 错词管理
      wrongWords: [],
      
      // 统计相关
      newWordCount: 0,
      recognitionCount: 0,
      startTime: null,
      
      // 学习小结
      summaryStats: {
        memorizedWords: 0,
        spelledWords: 0,
        consecutiveErrors: 0,
        consecutiveSpellingErrors: 0,
        earnedCoins: 0
      },
      
      // 延迟状态
      isShowingMeaningDelay: false,
      meaningDelayTimer: null
    };
  },
  computed: {
    // 当前生词数（新学的单词）
    currentNewWords() {
      return this.newWordCount;
    },
    
    // 预计复习时间（分钟）
    estimatedTime() {
      return Math.ceil(this.reviewWords.length * 0.5);
    }
  },
  watch: {
    value(newVal) {
      this.dialogVisible = newVal;
      if (newVal) {
        this.initReview();
      }
    },
    dialogVisible(newVal) {
      this.$emit('input', newVal);
    }
  },
  methods: {
    // 初始化复习
    initReview() {
      this.currentPhase = 'start';
      this.localReviewWords = [...this.reviewWords];
      this.currentWordIndex = 0;
      this.currentWord = this.localReviewWords[0] || {};
      this.showMeaning = false;
      this.forgetCount = 0;
      this.wrongWords = [];
      this.newWordCount = 0;
      this.recognitionCount = 0;
      this.startTime = Date.now();
      this.reviewProgress = 0;
      this.spellingProgress = 0;
      this.isShowingMeaningDelay = false;
      this.meaningDelayTimer = null;
    },

    // 开始复习
    startReview() {
      this.currentPhase = 'review';
    },

    // 第一部分：看词想义
    handleRemember() {
      this.recognitionCount++;
      this.newWordCount++; // 记住的单词算作新学
      this.reviewProgress = ((this.currentWordIndex + 1) / this.localReviewWords.length) * 100;
      
      // 显示中文意思几秒钟
      this.isShowingMeaningDelay = true;
      this.showMeaning = true;
      
      // 清除之前的定时器
      if (this.meaningDelayTimer) {
        clearTimeout(this.meaningDelayTimer);
      }
      
      // 2秒后自动进入下一个单词
      this.meaningDelayTimer = setTimeout(() => {
        this.isShowingMeaningDelay = false;
        this.nextWord();
      }, 2000);
    },

    handleForget() {
      this.forgetCount++;
      this.showMeaning = true;
      
      if (this.forgetCount >= 3) {
        // 连续忘记3次，移到后面
        const currentWord = this.localReviewWords[this.currentWordIndex];
        this.localReviewWords.splice(this.currentWordIndex, 1);
        this.localReviewWords.push(currentWord);
        this.forgetCount = 0;
        this.nextWord();
      }
    },

    handleSkip() {
      this.recognitionCount++;
      this.newWordCount++; // 跳过的单词也算作新学
      this.reviewProgress = ((this.currentWordIndex + 1) / this.localReviewWords.length) * 100;
      this.nextWord();
    },
    
    nextWord() {
      this.currentWordIndex++;
      this.showMeaning = false;
      this.forgetCount = 0;
      this.isShowingMeaningDelay = false;
      
      // 重置拼写测试相关状态
      this.userInput = '';
      this.showAnswer = false;
      this.isCorrect = false;
      this.timeLeft = 30;
      this.forcedSpellingIndex = 0;
      
      // 清除延迟定时器
      if (this.meaningDelayTimer) {
        clearTimeout(this.meaningDelayTimer);
        this.meaningDelayTimer = null;
      }
      
      // 清除拼写定时器
      if (this.spellingTimer) {
        clearInterval(this.spellingTimer);
        this.spellingTimer = null;
      }
      
      if (this.currentWordIndex >= this.localReviewWords.length) {
        // 复习完成，进入测试模式
        this.currentPhase = 'test-mode';
        return;
      }

      this.currentWord = this.localReviewWords[this.currentWordIndex];
      
      // 如果当前是拼写阶段，重新开始计时
      if (this.currentPhase === 'spelling') {
        this.startSpellingTimer();
      }
      // 注意：强制拼写阶段不需要定时器
    },

    // 开始拼写测试
    startSpellingTest(mode) {
      this.currentPhase = 'spelling';
      this.currentWordIndex = 0;
      this.spellingProgress = 0;
      
      if (mode === 'random') {
        // 随机打乱顺序
        this.localReviewWords = this.shuffleArray([...this.localReviewWords]);
      }
      
      this.currentWord = this.localReviewWords[0];
      this.userInput = '';
      this.showAnswer = false;
      this.startSpellingTimer();
    },

    startSpellingTimer() {
      this.timeLeft = 10;
      this.spellingTimer = setInterval(() => {
        this.timeLeft--;
        if (this.timeLeft <= 0) {
          this.handleTimeUp();
        }
      }, 1000);
    },

    stopSpellingTimer() {
      if (this.spellingTimer) {
        clearInterval(this.spellingTimer);
        this.spellingTimer = null;
      }
    },

    handleTimeUp() {
      this.stopSpellingTimer();
      this.isCorrect = false;
      this.showAnswer = true;
      this.recognitionCount++; // 时间到也算作识别
      this.addToWrongWords();
      
      // 3秒后自动切换下一个单词
      setTimeout(() => {
        this.nextSpellingWord();
      }, 3000);
    },
    
    checkSpelling() {
      this.stopSpellingTimer();
      const userWord = this.userInput.toLowerCase().trim();
      const correctWord = this.currentWord.english_word.toLowerCase();
      this.isCorrect = userWord === correctWord;
      this.showAnswer = true;
      
      // 拼写测试也算作识别
      this.recognitionCount++;
      
      if (this.isCorrect) {
        this.newWordCount++; // 拼写正确算作新学
        this.spellingProgress = ((this.currentWordIndex + 1) / this.localReviewWords.length) * 100; // 只有答对才增加进度条
      } else {
        this.addToWrongWords();
        // 拼写错误，添加到错词列表末尾，稍后重复测试
        this.localReviewWords.push({...this.currentWord, isRetry: true});
      }
      
      // 3秒后自动切换下一个单词
      setTimeout(() => {
        this.nextSpellingWord();
      }, 3000);
    },

    addToWrongWords() {
      // 拼写错误，加入错词列表
      const wrongWord = this.wrongWords.find(w => w.english_word === this.currentWord.english_word);
      if (wrongWord) {
        wrongWord.errorCount++;
        if (wrongWord.errorCount >= 3) {
          // 连续错误3次，立即开始强制拼写
          wrongWord.needForcedSpelling = true;
          this.startForcedSpelling([wrongWord]);
          return;
        }
      } else {
        this.wrongWords.push({
          ...this.currentWord,
          errorCount: 1,
          needForcedSpelling: false
        });
      }
    },
    
    nextSpellingWord() {
      this.currentWordIndex++;
      this.userInput = '';
      this.showAnswer = false;
      // 不在这里更新进度条，只有答对才更新
      
      if (this.currentWordIndex >= this.localReviewWords.length) {
        // 拼写测试完成，检查是否有需要强制拼写的词
        const forcedSpellingWords = this.wrongWords.filter(w => w.needForcedSpelling);
        if (forcedSpellingWords.length > 0) {
          this.startForcedSpelling(forcedSpellingWords);
        } else if (this.wrongWords.length > 0) {
          // 有错词，继续循环复习直到全对
          this.startWrongWordsReview();
        } else {
          // 所有词都正确，完成复习
          this.showSummary();
        }
        return;
      }

      this.currentWord = this.localReviewWords[this.currentWordIndex];
      // 开始新的10秒倒计时
      this.startSpellingTimer();
    },
    
    // 错词重复测试
    startWrongWordsReview() {
      // 将错词添加到测试列表末尾，继续循环复习
      const originalLength = this.localReviewWords.length;
      this.localReviewWords = [...this.localReviewWords, ...this.wrongWords];
      this.wrongWords = []; // 清空错词列表，重新开始统计
      this.currentWordIndex = originalLength; // 从错词开始
      this.currentWord = this.localReviewWords[this.currentWordIndex];
      
      // 完全重置拼写状态
      this.userInput = '';
      this.showAnswer = false;
      this.isCorrect = false;
      this.timeLeft = 30;
      this.forcedSpellingIndex = 0;
      
      // 清除之前的定时器
      if (this.spellingTimer) {
        clearInterval(this.spellingTimer);
        this.spellingTimer = null;
      }
      
      this.startSpellingTimer();
    },

    // 强制拼写
    startForcedSpelling(forcedWords) {
      this.currentPhase = 'forced-spelling';
      this.currentWordIndex = 0;
      this.wrongWords = forcedWords; // 只处理需要强制拼写的词
      this.currentWord = this.wrongWords[0];
      
      // 完全重置所有状态
      this.userInput = '';
      this.showAnswer = false;
      this.isCorrect = false;
      this.timeLeft = 30;
      this.forcedSpellingIndex = 0;
      
      // 清除之前的定时器
      if (this.spellingTimer) {
        clearInterval(this.spellingTimer);
        this.spellingTimer = null;
      }
    },

    handleForcedSpellingInput(event) {
      // 阻止默认行为，避免重复输入
      event.preventDefault();
      
      console.log('强制拼写输入:', event.key, '当前单词:', this.currentWord.english_word, '进度:', this.forcedSpellingIndex);
      
      const inputChar = event.key.toLowerCase();
      const expectedChar = this.currentWord.english_word[this.forcedSpellingIndex].toLowerCase();
      
      if (inputChar === expectedChar) {
        // 添加原始大小写的字母到显示
        this.userInput += this.currentWord.english_word[this.forcedSpellingIndex];
        this.forcedSpellingIndex++;
        
        console.log('输入正确，进度更新为:', this.forcedSpellingIndex);
        
        if (this.forcedSpellingIndex >= this.currentWord.english_word.length) {
          // 强制拼写完成
          console.log('强制拼写完成，准备切换单词');
          this.completeForcedSpelling();
        }
      } else {
        console.log('输入错误，期望:', expectedChar, '实际:', inputChar);
        this.$message.warning(`请输入字母: ${expectedChar.toUpperCase()}`);
      }
    },
    
    completeForcedSpelling() {
      console.log('开始完成强制拼写，当前错词数量:', this.wrongWords.length);
      
      // 从错词列表中移除当前单词，并添加到主列表末尾
      const currentWordIndex = this.wrongWords.findIndex(w => w.english_word === this.currentWord.english_word);
      if (currentWordIndex !== -1) {
        const completedWord = this.wrongWords.splice(currentWordIndex, 1)[0];
        // 将完成的词添加到主列表末尾，供最后复习
        this.localReviewWords.push(completedWord);
        // 强制拼写完成也算作新学
        this.newWordCount++;
        this.recognitionCount++;
        console.log('单词完成，移除后错词数量:', this.wrongWords.length);
      }
      
      if (this.wrongWords.length > 0) {
        // 还有错词，继续强制拼写
        console.log('还有错词，切换到下一个:', this.wrongWords[0].english_word);
        this.currentWord = this.wrongWords[0] || {};
        // 重置所有状态
        this.userInput = '';
        this.forcedSpellingIndex = 0;
        this.showAnswer = false;
        this.isCorrect = false;
        this.timeLeft = 30;
      } else {
        // 所有错词都完成，检查是否还有需要重复测试的词
        if (this.localReviewWords.length > this.reviewWords.length) {
          // 有完成的词需要最后复习，回到拼写测试
          this.currentPhase = 'spelling';
          this.currentWordIndex = this.reviewWords.length; // 从新添加的词开始
          this.currentWord = this.localReviewWords[this.currentWordIndex];
          
          // 完全重置拼写状态，确保从强制拼写模式完全退出
          this.userInput = '';
          this.showAnswer = false;
          this.isCorrect = false;
          this.timeLeft = 30;
          this.forcedSpellingIndex = 0;
          
          // 清除强制拼写相关的定时器
          if (this.spellingTimer) {
            clearInterval(this.spellingTimer);
            this.spellingTimer = null;
          }
          
          this.startSpellingTimer();
        } else {
          this.showSummary();
        }
      }
    },

    // 显示学习小结
    showSummary() {
      this.currentPhase = 'summary';
      this.calculateSummaryStats();
    },

    calculateSummaryStats() {
      // 计算实际学习的唯一单词数量
      const uniqueWordsLearned = new Set(this.localReviewWords.map(word => word.id || word.word_id)).size;
      
      console.log('记忆曲线金币计算调试:');
      console.log('- 总单词数:', this.localReviewWords.length);
      console.log('- 唯一单词数:', uniqueWordsLearned);
      console.log('- 记住次数:', this.newWordCount);
      console.log('- 拼写次数:', this.recognitionCount);
      console.log('- 计算金币:', uniqueWordsLearned * 3);
      
      this.summaryStats = {
        memorizedWords: this.newWordCount, // 记住的单词数
        spelledWords: this.recognitionCount, // 拼写测试的单词数
        consecutiveErrors: this.forgetCount, // 连续识别错误数
        consecutiveSpellingErrors: this.wrongWords.length, // 连续拼写错误数
        totalReviewTime: Math.floor((Date.now() - this.startTime) / 1000), // 总复习时间(秒)
        accuracy: this.localReviewWords.length > 0 ? 
          Math.round(((this.localReviewWords.length - this.wrongWords.length) / this.localReviewWords.length) * 100) : 100,
        earnedCoins: Math.max(1, Math.floor(uniqueWordsLearned * 3)) // 按实际学习的唯一单词数量计算金币
      };
    },

    // 完成复习
    async completeReview() {
      try {
        // 计算实际学习的唯一单词数量
        const uniqueWordsLearned = new Set(this.localReviewWords.map(word => word.id || word.word_id)).size;
        
        // 提交学习数据
        const learningData = {
          words: this.localReviewWords.map(word => ({
            word_id: word.id || word.word_id,
            english_word: word.english_word,
            chinese_meaning: word.chinese_meaning,
            is_correct: !this.wrongWords.find(w => w.english_word === word.english_word),
            review_time: new Date().toISOString()
          })),
          total_words: uniqueWordsLearned, // 使用唯一单词数量
          correct_words: this.newWordCount, // 使用实际记住的单词数
          wrong_words: this.wrongWords.length,
          study_time: this.startTime ? Math.floor((Date.now() - this.startTime) / 1000) : 0, // 秒
          study_date: new Date().toISOString().split('T')[0],
          earned_coins: this.summaryStats.earnedCoins,
          // 添加更多统计字段
          reviewed_count: uniqueWordsLearned, // 使用唯一单词数量
          new_words_count: this.newWordCount,
          accuracy: this.summaryStats.accuracy
        };
        
        await homeApi.submitLearning(learningData);
        this.$message.success(`学习数据已保存！获得 ${this.summaryStats.earnedCoins} 金币！`);
      } catch (error) {
        console.error("提交学习数据失败：", error);
        this.$message.error("保存学习数据失败");
      }
    
      this.dialogVisible = false;
      this.$emit('review-completed');
    },

    // 处理弹窗关闭
    handleClose() {
      // 如果正在学习过程中，需要确认
      if (this.currentPhase !== 'start' && this.currentPhase !== 'summary') {
        this.$confirm('确定要退出复习吗？当前进度将不会保存。', '确认退出', {
          confirmButtonText: '确定退出',
          cancelButtonText: '继续复习',
          type: 'warning'
        }).then(() => {
          this.dialogVisible = false;
          this.$emit('review-completed');
        }).catch(() => {
          // 用户取消，不做任何操作
        });
      } else {
        // 在开始页面或总结页面，直接关闭
        this.dialogVisible = false;
        this.$emit('review-completed');
      }
    },

    // 工具方法
    playPronunciation() {
      // 播放单词发音
      const pronunciationUrl = this.currentWord.pronunciation_url;
      if (pronunciationUrl) {
        // 使用音频工具函数
        const { playAudio } = require('@/utils/audioUtils');
        playAudio(pronunciationUrl).catch(() => {
          console.error("播放音频失败");
          this.$message.warning("音频播放失败");
        });
      } else {
        this.$message.info("暂无发音文件");
      }
    },

    shuffleArray(array) {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    }
  }
};
</script>

<style scoped>
.memory-curve-dialog .el-dialog {
  border-radius: 12px;
  overflow: hidden;
  width: 800px !important;
  height: 600px !important;
  max-width: 800px !important;
  max-height: 600px !important;
}

.memory-curve-dialog .el-dialog__body {
  height: 500px !important;
  overflow-y: auto;
}

/* 开始页面样式 */
.start-phase {
  text-align: center;
}

.phase-header {
  margin-bottom: 30px;
}

.phase-header h2 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.phase-header p {
  color: #7f8c8d;
  font-size: 14px;
}

.start-content {
  padding: 20px;
}

.curve-intro {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  text-align: left;
}

.intro-text h3 {
  color: #2c3e50;
  margin-bottom: 15px;
}

.intro-text p {
  color: #666;
  line-height: 1.6;
  margin-bottom: 10px;
}

.stats-info {
  display: flex;
  justify-content: space-around;
  margin-top: 20px;
}

.stat-item {
  text-align: center;
}

.stat-label {
  display: block;
  color: #666;
  font-size: 14px;
  margin-bottom: 5px;
}

.stat-value {
  display: block;
  color: #409EFF;
  font-size: 18px;
  font-weight: bold;
}

/* 复习阶段样式 */
.review-phase {
  padding: 20px;
}

.progress-info {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #409EFF, #67c23a);
  transition: width 0.3s ease;
}

.word-display {
  margin: 30px 0;
}

.word-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  margin-bottom: 20px;
}

.word-text {
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 15px;
}

.word-info {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
}

.phonetic-info {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-bottom: 10px;
}

.phonetic {
  font-size: 16px;
  opacity: 0.9;
}

.meaning-display {
  background: #f0f9ff;
  border: 2px solid #409EFF;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
}

.meaning-card h3 {
  color: #409EFF;
  font-size: 24px;
  margin-bottom: 15px;
}

.example-sentence {
  color: #666;
  font-style: italic;
  margin-bottom: 10px;
}

.chinese-sentence {
  color: #999;
  font-size: 14px;
}

.question-area {
  text-align: center;
  padding: 40px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 20px;
}

.question-mark {
  font-size: 48px;
  color: #409EFF;
  margin-bottom: 15px;
}

.question-area p {
  color: #666;
  font-style: italic;
  margin: 0;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
}

.delay-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #409EFF;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 20px;
  background: rgba(64, 158, 255, 0.1);
  border-radius: 20px;
  border: 1px solid rgba(64, 158, 255, 0.3);
}

.action-buttons .el-button {
  min-width: 120px;
}

/* 测试模式选择 */
.test-mode-selection {
  text-align: center;
  padding: 20px;
}

.test-options {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin: 30px 0;
}

.option-card {
  background: white;
  border: 2px solid #e4e7ed;
  border-radius: 12px;
  padding: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 200px;
}

.option-card:hover {
  border-color: #409EFF;
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(64, 158, 255, 0.15);
}

.option-icon {
  font-size: 48px;
  color: #409EFF;
  margin-bottom: 15px;
}

.option-card h3 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.option-card p {
  color: #666;
  font-size: 14px;
}

/* 拼写测试样式 */
.spelling-phase {
  padding: 20px;
}

.spelling-interface {
  margin: 30px 0;
}

.word-info {
  text-align: center;
  margin-bottom: 30px;
}

.word-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.word-row h3 {
  color: #2c3e50;
  font-size: 24px;
  margin: 0;
}

.input-area {
  text-align: center;
  margin-bottom: 20px;
}

.timer-display {
  margin-bottom: 20px;
}

.timer-text {
  display: block;
  color: #666;
  margin-bottom: 10px;
}

.timer-bar {
  width: 200px;
  height: 8px;
  background: #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 auto;
}

.timer-fill {
  height: 100%;
  background: linear-gradient(90deg, #f56c6c, #e6a23c);
  transition: width 1s linear;
}

.input-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}

.spelling-input {
  width: 300px;
  padding: 15px;
  font-size: 18px;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  text-align: center;
}

.spelling-input:focus {
  border-color: #409EFF;
  outline: none;
}

.spelling-input.correct {
  border-color: #67c23a;
  background: #f0f9ff;
}

.spelling-input.incorrect {
  border-color: #f56c6c;
  background: #fef0f0;
}

.answer-display {
  text-align: center;
}

.answer-result {
  font-size: 18px;
  font-weight: bold;
  padding: 15px;
  border-radius: 8px;
}

.answer-result.correct {
  color: #67c23a;
  background: #f0f9ff;
}

.answer-result.incorrect {
  color: #f56c6c;
  background: #fef0f0;
}

.correct-answer {
  color: #409EFF;
  font-weight: bold;
}

/* 强制拼写样式 */
.forced-spelling-phase {
  padding: 20px;
}

.forced-spelling-interface {
  margin: 30px 0;
}

.forced-spelling-input {
  width: 400px;
  padding: 15px;
  font-size: 18px;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  text-align: center;
  margin-bottom: 15px;
}

.expected-char {
  color: #409EFF;
  font-size: 16px;
  font-weight: bold;
}

/* 学习小结样式 */
.summary-phase {
  padding: 20px;
}

.summary-content {
  text-align: center;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-item {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid #409EFF;
}

.stat-label {
  display: block;
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
}

.stat-value {
  display: block;
  color: #2c3e50;
  font-size: 18px;
  font-weight: bold;
}

.stat-value.error {
  color: #f56c6c;
}

.stat-value.success {
  color: #67c23a;
}

.stat-value.coins {
  color: #e6a23c;
  font-weight: bold;
}

.reward-section {
  text-align: center;
  margin: 30px 0;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
}

.reward-text {
  font-size: 18px;
  font-weight: bold;
}

.reward-text i {
  margin-left: 8px;
  font-size: 20px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .memory-curve-dialog .el-dialog {
    width: 95% !important;
    margin: 10px auto !important;
  }
  
  .start-content {
    padding: 10px;
  }
  
  .test-options {
    flex-direction: column;
    align-items: center;
  }
  
  .option-card {
    width: 100%;
    max-width: 300px;
  }
  
  .action-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .action-buttons .el-button {
    width: 200px;
  }
}
</style>
